// JOBE-0167.2: MemoryFilterBar — six-facet filter bar above the list.
//
// Facets: subject (urn text) · type (select) · lifecycle (select) ·
//         owner (urn text) · tier (select) · window (range pair).
//
// Debounce (300ms) lives in the parent (MemoriesTabBody). This component is
// a controlled view over the filter state.

// JOBE-0076.1 memory-type taxonomy (10 values). UI label = "Type"; on the
// wire this maps to the envelope's `kind` field (see MemoriesTabBody fetch).
export const MEMORY_TYPE_VALUES = [
  'preference',
  'knowledge',
  'procedure-lesson',
  'observation',
  'belief',
  'summary',
  'decision',
  'event-trace',
  'feedback',
  'obligation',
];

// JOBE-0076.1 lifecycle states.
export const MEMORY_LIFECYCLE_VALUES = [
  'active',
  'superseded',
  'tombstoned',
  'candidate-implicit',
];

// JOBE-0076.1 memory tiers (working/episodic/semantic/procedural).
export const MEMORY_TIER_VALUES = [
  'working',
  'episodic',
  'semantic',
  'procedural',
];

export const DEFAULT_FILTERS = Object.freeze({
  subject: '',
  type: '',
  lifecycle: '',
  owner: '',
  tier: '',
  windowFrom: defaultWindowFromIso(),
  windowTo: '',
});

// Default window = last 30 days (just the from-date; to-date open = now).
function defaultWindowFromIso() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

/**
 * @param {object} props
 * @param {object} props.filters     current filter state
 * @param {(patch: object) => void} props.onChange   merge-patch update
 * @param {() => void} props.onReset reset all facets to default + refetch
 */
export function MemoryFilterBar({ filters, onChange, onReset }) {
  return (
    <div className="memory-filter-bar" data-testid="memory-filter-bar" role="group" aria-label="Memory filters">
      <label className="memory-filter-bar__facet" data-facet="subject">
        <span className="memory-filter-bar__label">Subject</span>
        <input
          type="text"
          aria-label="Subject"
          placeholder="urn:jobe:person:…"
          value={filters.subject}
          onChange={(e) => onChange({ subject: e.target.value })}
          data-testid="memory-filter-subject"
        />
      </label>

      <label className="memory-filter-bar__facet" data-facet="type">
        <span className="memory-filter-bar__label">Type</span>
        <select
          aria-label="Type"
          value={filters.type}
          onChange={(e) => onChange({ type: e.target.value })}
          data-testid="memory-filter-type"
        >
          <option value="">All types</option>
          {MEMORY_TYPE_VALUES.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      </label>

      <label className="memory-filter-bar__facet" data-facet="lifecycle">
        <span className="memory-filter-bar__label">Lifecycle</span>
        <select
          aria-label="Lifecycle"
          value={filters.lifecycle}
          onChange={(e) => onChange({ lifecycle: e.target.value })}
          data-testid="memory-filter-lifecycle"
        >
          <option value="">All lifecycles</option>
          {MEMORY_LIFECYCLE_VALUES.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      </label>

      <label className="memory-filter-bar__facet" data-facet="owner">
        <span className="memory-filter-bar__label">Owner</span>
        <input
          type="text"
          aria-label="Owner"
          placeholder="urn:jobe:agent:…"
          value={filters.owner}
          onChange={(e) => onChange({ owner: e.target.value })}
          data-testid="memory-filter-owner"
        />
      </label>

      <label className="memory-filter-bar__facet" data-facet="tier">
        <span className="memory-filter-bar__label">Tier</span>
        <select
          aria-label="Tier"
          value={filters.tier}
          onChange={(e) => onChange({ tier: e.target.value })}
          data-testid="memory-filter-tier"
        >
          <option value="">All tiers</option>
          {MEMORY_TIER_VALUES.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      </label>

      <label className="memory-filter-bar__facet" data-facet="window">
        <span className="memory-filter-bar__label">Window</span>
        <span className="memory-filter-bar__window-range">
          <input
            type="date"
            aria-label="Window from"
            value={filters.windowFrom}
            onChange={(e) => onChange({ windowFrom: e.target.value })}
            data-testid="memory-filter-window-from"
          />
          <span aria-hidden="true"> — </span>
          <input
            type="date"
            aria-label="Window to"
            value={filters.windowTo}
            onChange={(e) => onChange({ windowTo: e.target.value })}
            data-testid="memory-filter-window-to"
          />
        </span>
      </label>

      <button
        type="button"
        className="memory-filter-bar__reset"
        data-testid="memory-filter-reset"
        onClick={onReset}
      >
        Reset
      </button>
    </div>
  );
}

export default MemoryFilterBar;
